
import { doc, getDoc, setDoc, updateDoc, arrayUnion, arrayRemove, serverTimestamp } from 'firebase/firestore';
import { db } from '@/utils/firebase';
import { USERS_COLLECTION } from '../constants';
// Assuming User and Member types are derived from Zod schemas in src/schemas/user.ts
// If Member is a distinct schema, it should be imported accordingly.
// For now, we'll primarily use userSchema for validation of user documents.
import { userSchema, UserDataInput } from '@/schemas/user'; // UserDataInput is the Zod-inferred type for data to be written
import { Member } from '@/types/schema'; // Keep Member if it's a distinct manual type used for specific structures

/**
 * Update a user document in Firestore
 */
export const updateUser = async (userId: string, userData: Partial<UserDataInput | Member>): Promise<boolean> => {
  try {
    console.log(`Updating user ${userId} with data:`, userData);
    
    if (!userId) {
      console.error('Invalid userId provided for update');
      return false;
    }

    const userRef = doc(db, USERS_COLLECTION, userId);
    const userDoc = await getDoc(userRef);

    if (!userDoc.exists()) {
      console.log(`User ${userId} does not exist, creating new document (or updating if it's an auth user without a DB doc yet)`);

      // For new users, set a default role if not provided
      const newUserDataWithDefaults = { ...userData };
      if (!('role' in newUserDataWithDefaults) && 'email' in newUserDataWithDefaults) { // Ensure email exists, common for new user objects
        console.log('Setting default role "Manager" for new user');
        (newUserDataWithDefaults as any).role = 'Manager'; // Consider if role should be part of schema or handled differently
      }
      
      // Validate data against the full user schema for creation
      const validationResult = userSchema.safeParse({
        ...newUserDataWithDefaults,
        id: userId, // Ensure id is part of the validated object
        userId: userId, // Ensure userId is part of the validated object
      });

      if (!validationResult.success) {
        console.error(`Validation failed for creating user ${userId}:`, validationResult.error.flatten());
        return false;
      }
      
      await setDoc(userRef, {
        ...validationResult.data,
        portfolios: [], // Ensure portfolios is initialized as an empty array
        businesses: [], // Ensure businesses is initialized as an empty array
        updatedAt: serverTimestamp(),
        createdAt: serverTimestamp()
      });
    } else {
      // Validate partial data against the partial user schema for updates
      const validationResult = userSchema.partial().safeParse(userData);
      if (!validationResult.success) {
        console.error(`Validation failed for updating user ${userId}:`, validationResult.error.flatten());
        return false;
      }

      // For existing users, don't override role if it's not explicitly provided
      // This logic might be better handled by ensuring `userData` is already complete or by specific update functions
      const updateDataPayload = { ...validationResult.data };
      const existingData = userDoc.data();
      if ('role' in existingData && !('role' in updateDataPayload) && updateDataPayload.role === undefined) {
        console.log(`Preserving existing role: ${existingData.role} for user ${userId}`);
        // No need to explicitly add existingData.role if not changing, as it's not in updateDataPayload
      }
      
      await updateDoc(userRef, {
        ...updateDataPayload,
        updatedAt: serverTimestamp()
      });
    }

    console.log(`Successfully updated/created user ${userId}`);
    return true;
  } catch (error) {
    console.error('Error updating user:', error);
    return false;
  }
};

/**
 * Update the current authenticated user's document
 */
export const updateCurrentUser = async (userData: Partial<UserDataInput | Member>): Promise<boolean> => {
  try {
    // Fix: Check for id first, then userId, ensuring we handle both User and Member types
    // This assumes 'id' or 'userId' will be present in the partial data for identification.
    // For user updates, 'id' (document ID) is canonical.
    const userId = userData.id || (userData as any).userId; 
    
    if (!userId) {
      console.error('No user ID (id or userId field) provided in userData for current user update');
      return false;
    }
    
    return await updateUser(userId, userData);
  } catch (error) {
    console.error('Error updating current user:', error);
    return false;
  }
};

/**
 * Add a business to a user's list of businesses
 */
export const addBusinessToUser = async (userId: string, businessId: string): Promise<boolean> => {
  try {
    console.log(`Adding business ${businessId} to user ${userId}`);
    
    if (!userId || !businessId) {
      console.error('Invalid userId or businessId provided');
      return false;
    }
    
    const userRef = doc(db, USERS_COLLECTION, userId);
    const userDoc = await getDoc(userRef);
    
    if (!userDoc.exists()) {
      console.log(`User ${userId} does not exist, cannot add business`);
      return false;
    }
    
    await updateDoc(userRef, {
      businesses: arrayUnion(businessId),
      updatedAt: serverTimestamp()
    });
    
    console.log(`Successfully added business ${businessId} to user ${userId}`);
    return true;
  } catch (error) {
    console.error('Error adding business to user:', error);
    return false;
  }
};

/**
 * Remove a business from a user's list of businesses
 */
export const removeBusinessFromUser = async (userId: string, businessId: string): Promise<boolean> => {
  try {
    const userRef = doc(db, USERS_COLLECTION, userId);
    await updateDoc(userRef, {
      businesses: arrayRemove(businessId),
      updatedAt: serverTimestamp()
    });
    return true;
  } catch (error) {
    console.error('Error removing business from user:', error);
    return false;
  }
};

// Additionally export from auth file for backward compatibility
export * from './auth';
